import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Building2,
  Store,
  Mail,
  BarChart3,
  Home as HomeIcon,
  LogOut,
  Copy,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import Seo from "@/components/foundation/Seo";
import Logo from "@/components/brand/Logo";
import Button from "@/components/foundation/Button";
import { adminApi, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const TOKEN_KEY = "zenme_admin_token";

/* --------------------- Tabs config --------------------- */
const TABS = [
  { key: "overview", label: "Overview", icon: HomeIcon },
  { key: "merchant", label: "Merchant Waitlist", icon: Users },
  { key: "partners", label: "Funding Partners", icon: Building2 },
  { key: "marketplace", label: "Marketplace Waitlist", icon: Store },
  { key: "contact", label: "Contact", icon: Mail },
  { key: "attribution", label: "Attribution", icon: BarChart3 },
];

const COLLECTION_LABELS = {
  merchant_waitlist: "Merchant Waitlist",
  funding_partners: "Funding Partners",
  marketplace_waitlist: "Marketplace",
  contact_submissions: "Contact",
};

/* Column definitions per kind */
const COLUMNS = {
  merchant: [
    { key: "email", label: "Email" },
    { key: "source", label: "Source" },
    { key: "created_at", label: "Created", format: fmtDate },
  ],
  partners: [
    { key: "company_name", label: "Company" },
    { key: "contact_name", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "monthly_volume", label: "Volume" },
    { key: "created_at", label: "Created", format: fmtDate },
  ],
  marketplace: [
    { key: "business_name", label: "Business" },
    { key: "business_email", label: "Email" },
    { key: "source", label: "Source" },
    { key: "created_at", label: "Created", format: fmtDate },
  ],
  contact: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "reason", label: "Reason" },
    { key: "created_at", label: "Created", format: fmtDate },
  ],
};

const PAGE_SIZE = 25;

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(v);
  }
}

/* --------------------- Main Dashboard --------------------- */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [active, setActive] = useState("overview");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) {
      navigate("/admin", { replace: true });
      return;
    }
    setToken(t);
    setReady(true);
  }, [navigate]);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    navigate("/admin", { replace: true });
  };

  if (!ready) return null;

  return (
    <div className="min-h-screen bg-bg-base text-white">
      <Seo
        title="ZenMe Capital · Admin Dashboard"
        description="Internal admin dashboard."
        path="/admin/dashboard"
      />

      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b border-white/[0.06] bg-bg-base/85 backdrop-blur-xl"
        data-testid="admin-topbar"
      >
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo height={22} />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-brand-emerald">
              Admin
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            leftIcon={<LogOut className="h-3.5 w-3.5" />}
            data-testid="admin-signout"
          >
            Sign out
          </Button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px] flex-col gap-6 px-4 py-8 sm:px-6 lg:flex-row">
        {/* Sidebar */}
        <aside
          className="lg:w-56 lg:flex-shrink-0"
          data-testid="admin-sidebar"
        >
          <nav className="flex gap-1 overflow-x-auto lg:flex-col lg:gap-0.5 lg:overflow-visible">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  className={cn(
                    "flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-white/[0.05] text-white"
                      : "text-text-secondary hover:bg-white/[0.03] hover:text-white",
                  )}
                  data-testid={`admin-tab-${t.key}`}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4",
                      isActive ? "text-brand-emerald" : "",
                    )}
                    strokeWidth={1.75}
                  />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1" data-testid="admin-main">
          {active === "overview" && <OverviewView token={token} />}
          {active === "merchant" && (
            <ListView kind="merchant" title="Merchant Waitlist" token={token} />
          )}
          {active === "partners" && (
            <ListView kind="partners" title="Funding Partners" token={token} />
          )}
          {active === "marketplace" && (
            <ListView kind="marketplace" title="Marketplace Waitlist" token={token} />
          )}
          {active === "contact" && (
            <ListView kind="contact" title="Contact Submissions" token={token} />
          )}
          {active === "attribution" && <AttributionView token={token} />}
        </main>
      </div>
    </div>
  );
}

/* --------------------- Overview --------------------- */
function OverviewView({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.overview(token);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled)
          setErr(e instanceof ApiError ? e.message : "Failed to load.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const cards = [
    {
      label: "Merchant Waitlist",
      value: data.counts.merchant_waitlist,
      icon: Users,
      key: "merchant_waitlist",
    },
    {
      label: "Funding Partners",
      value: data.counts.funding_partners,
      icon: Building2,
      key: "funding_partners",
    },
    {
      label: "Marketplace",
      value: data.counts.marketplace_waitlist,
      icon: Store,
      key: "marketplace_waitlist",
    },
    {
      label: "Contact",
      value: data.counts.contact_submissions,
      icon: Mail,
      key: "contact_submissions",
    },
  ];

  return (
    <div className="flex flex-col gap-8" data-testid="admin-overview">
      <div>
        <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
          Overview
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Total submissions across every collection.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/[0.06] bg-bg-card p-5"
              data-testid={`admin-stat-${c.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-text-muted">
                  {c.label}
                </span>
                <Icon className="h-4 w-4 text-brand-emerald" strokeWidth={1.75} />
              </div>
              <div className="mt-3 font-display text-3xl font-semibold tabular-nums text-white">
                {c.value.toLocaleString()}
              </div>
            </motion.div>
          );
        })}
      </div>

      <section className="rounded-2xl border border-white/[0.06] bg-bg-card">
        <div className="border-b border-white/[0.05] px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-white">
            Recent Activity
          </h2>
        </div>
        {data.recent.length === 0 ? (
          <p className="p-5 text-sm text-text-muted">No submissions yet.</p>
        ) : (
          <ul className="divide-y divide-white/[0.05]" data-testid="admin-recent-list">
            {data.recent.map((r, i) => (
              <li
                key={`${r.kind}-${r.id}-${i}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                data-testid="admin-recent-item"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-brand-emerald">
                    {r.kind}
                  </span>
                  <span className="text-sm text-white">{r.preview || "—"}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    {r.source || "—"}
                  </span>
                  <span className="text-xs text-text-muted">
                    {fmtDate(r.created_at)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* --------------------- List view --------------------- */
function ListView({ kind, title, token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [copiedId, setCopiedId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await adminApi.list(token, kind, 100, 0);
        if (!cancelled) {
          setItems(res.items || []);
          setTotal(res.total || 0);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof ApiError ? e.message : "Failed to load.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, kind]);

  const cols = COLUMNS[kind] || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const fields = [
        row.email,
        row.business_email,
        row.name,
        row.contact_name,
        row.company_name,
        row.business_name,
      ].filter(Boolean);
      return fields.some((f) => String(f).toLowerCase().includes(q));
    });
  }, [items, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const copyEmail = (email, id) => {
    if (!email) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(email).catch(() => {});
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(""), 1400);
  };

  return (
    <div className="flex flex-col gap-6" data-testid={`admin-list-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
            {title}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Total: <span className="font-medium tabular-nums text-white">{total}</span>
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            placeholder="Filter by email or name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] py-2 pl-9 pr-3 text-sm text-white placeholder:text-text-muted outline-none transition-colors focus:border-brand-emerald/60"
            data-testid={`admin-search-${kind}`}
          />
        </div>
      </div>

      {err && <ErrorState message={err} />}
      {loading ? (
        <LoadingState />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-bg-card p-10 text-center">
          <p className="text-sm text-text-muted">
            {items.length === 0 ? "No submissions yet." : "No matches for that filter."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-bg-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.05] bg-white/[0.02]">
                  {cols.map((c) => (
                    <th
                      key={c.key}
                      className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-widest text-text-muted"
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-text-muted">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {pageItems.map((row) => {
                  const emailField = row.email || row.business_email;
                  return (
                    <tr
                      key={row.id}
                      className="transition-colors hover:bg-white/[0.02]"
                      data-testid={`admin-row-${kind}`}
                    >
                      {cols.map((c) => {
                        const raw = row[c.key];
                        const value = c.format ? c.format(raw) : raw;
                        return (
                          <td
                            key={c.key}
                            className="max-w-[220px] truncate px-4 py-3 text-white/90"
                          >
                            {value || <span className="text-text-muted">—</span>}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => copyEmail(emailField, row.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] px-2 py-1 text-[11px] text-text-secondary transition-colors hover:border-brand-emerald/50 hover:text-brand-emerald"
                          data-testid="admin-copy-email"
                        >
                          <Copy className="h-3 w-3" />
                          {copiedId === row.id ? "Copied" : "Copy email"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between border-t border-white/[0.05] px-4 py-3">
              <span className="text-[11px] text-text-muted">
                Page {page + 1} of {pageCount} · showing {pageItems.length} of {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/[0.08] text-text-secondary transition-colors hover:border-brand-emerald/50 hover:text-brand-emerald disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid={`admin-page-prev-${kind}`}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/[0.08] text-text-secondary transition-colors hover:border-brand-emerald/50 hover:text-brand-emerald disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid={`admin-page-next-${kind}`}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------- Attribution view --------------------- */
function AttributionView({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminApi.attribution(token);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled)
          setErr(e instanceof ApiError ? e.message : "Failed to load.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const totalBySource = data.total_by_source || {};
  const rows = Object.entries(totalBySource).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...rows.map(([, v]) => v));

  const collectionKeys = [
    "merchant_waitlist",
    "funding_partners",
    "marketplace_waitlist",
    "contact_submissions",
  ];

  const allSources = Array.from(
    new Set(
      collectionKeys.flatMap((k) => Object.keys(data[k] || {})),
    ),
  ).sort();

  return (
    <div className="flex flex-col gap-8" data-testid="admin-attribution">
      <div>
        <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
          Attribution
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Conversion counts across every source on the site.
        </p>
      </div>

      {/* Bar chart */}
      <section className="rounded-2xl border border-white/[0.06] bg-bg-card p-6">
        <h2 className="mb-6 font-mono text-[10px] uppercase tracking-widest text-text-muted">
          Total by Source
        </h2>
        {rows.length === 0 ? (
          <p className="text-sm text-text-muted">No submissions yet.</p>
        ) : (
          <ul
            className="flex flex-col gap-4"
            data-testid="admin-attribution-chart"
          >
            {rows.map(([source, count]) => (
              <li key={source} className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-white">
                    {source}
                  </span>
                  <span className="font-mono text-xs tabular-nums text-brand-emerald">
                    {count}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.05]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(count / max) * 100}%` }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #00C853 0%, #00E676 100%)",
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Breakdown table */}
      <section className="overflow-hidden rounded-2xl border border-white/[0.06] bg-bg-card">
        <div className="border-b border-white/[0.05] px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-white">
            Source × Collection
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-attribution-table">
            <thead>
              <tr className="border-b border-white/[0.05] bg-white/[0.02]">
                <th className="px-4 py-3 text-left text-[10px] font-medium uppercase tracking-widest text-text-muted">
                  Source
                </th>
                {collectionKeys.map((k) => (
                  <th
                    key={k}
                    className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-text-muted"
                  >
                    {COLLECTION_LABELS[k]}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-brand-emerald">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {allSources.length === 0 ? (
                <tr>
                  <td
                    colSpan={collectionKeys.length + 2}
                    className="px-4 py-5 text-center text-sm text-text-muted"
                  >
                    No submissions yet.
                  </td>
                </tr>
              ) : (
                allSources.map((source) => {
                  const values = collectionKeys.map((k) => data[k]?.[source] || 0);
                  const total = values.reduce((a, b) => a + b, 0);
                  return (
                    <tr key={source}>
                      <td className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-white">
                        {source}
                      </td>
                      {values.map((v, i) => (
                        <td
                          key={i}
                          className="px-4 py-3 text-right font-mono text-xs tabular-nums text-white/90"
                        >
                          {v || <span className="text-text-muted">—</span>}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-brand-emerald">
                        {total}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* --------------------- helpers --------------------- */
function LoadingState() {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-bg-card p-10 text-center">
      <p className="text-sm text-text-muted">Loading…</p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-6">
      <p className="text-sm text-red-300">{message}</p>
    </div>
  );
}
